import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FETCH_SIZE_CARD_DATA } from "../../DataTagConstants";
import { Doughnut } from "react-chartjs-2";
import "chart.js/auto";
import "./DataEstateSizeCard.scss";
import { filesize } from "filesize";
import { numberFormatter } from "../../../../components/common/Dashboard/utils";

const GREY = "#e5e5e5";

const COLORS = {
  PROCESSED: "#4caf50",
  SENSITIVE: "#ff3366",
  NO_RISK: "#9b59b6",
  REGULATED: "#ffa500",
  PUBLIC: "#0090ff",
  COMPLETED: "#00a98f",
  FAILED: "#d93025",
  UNSUPPORTED: "#9aa0a6",
  INPROGRESS: "#f5a000",
  NOT_TAGGED: "#e5e5e5",
};

const LABELS = {
  PROCESSED: "Processed",
  NOT_PROCESSED: "Not Processed",
  SENSITIVE: "Sensitive",
  NO_RISK: "No Risk",
  REGULATED: "Regulated",
  NOT_REGULATED: "Remaining",
  PUBLIC: "Public",
  NOT_PUBLIC: "Remaining",
  COMPLETED: "Successfully tagged",
  FAILED: "Failed to tag",
  UNSUPPORTED: "Tagging unsupported",
  INPROGRESS: "Tagging in progress",
  NOT_TAGGED: "Not tagged",
  NO_TAGS: "Not tagged",
};

const pct = (n) => {
  const v = Number(n) || 0;
  return Number.isInteger(v) ? v : Number(v.toFixed(2));
};

// Tag ring: every arc is a share of ALL files.
//   tagged arcs  = the tagRing entries (completed / failed / unsupported / in progress)
//   untagged arc = total files - total tagged files
const buildTagRing = (tagRing = [], totalTagged = 0, totalFiles = 0) => {
  const files = Number(totalFiles) || 0;
  if (files <= 0) return [{ label: "NO_TAGS", count: 0, percent: 100 }];

  const untagged = Math.max(0, files - (Number(totalTagged) || 0));

  const arcs = tagRing
    .filter((r) => Number(r.count) > 0)
    .map((r) => ({
      label: r.label,
      count: Number(r.count),
      percent: (Number(r.count) / files) * 100,
    }));

  if (untagged > 0) {
    arcs.push({
      label: "NOT_TAGGED",
      count: untagged,
      percent: (untagged / files) * 100,
    });
  }
  return arcs;
};

// Tagged % = successfully tagged files / total sensitive files
const successTaggedPct = (tagRing = [], sensitivityRing = []) => {
  const success = Number(tagRing.find((r) => r.label === "COMPLETED")?.count) || 0;
  const sensitive =
    Number(sensitivityRing.find((r) => r.label === "SENSITIVE")?.count) || 0;
  return sensitive > 0 ? pct((success / sensitive) * 100) : 0;
};

const getOrCreateTooltipEl = (wrapper) => {
  let el = wrapper.querySelector(".de-chart-tooltip-v2");
  if (!el) {
    el = document.createElement("div");
    el.className = "de-chart-tooltip-v2";
    Object.assign(el.style, {
      position: "absolute",
      zIndex: 30,
      pointerEvents: "none",
      minWidth: "190px",
      padding: "10px 12px",
      borderRadius: "10px",
      background: "#1f2433",
      color: "#fff",
      boxShadow: "0 6px 20px rgba(20, 26, 42, 0.28)",
      fontSize: "12px",
      lineHeight: "1.35",
      whiteSpace: "nowrap",
      opacity: 0,
      transition: "opacity 0.12s ease",
    });
    wrapper.appendChild(el);
  }
  return el;
};

// Card tooltip: ring name, the hovered slice (exact count + exact %), and the ring total.
const externalTooltipHandler = (context) => {
  const { chart, tooltip } = context;
  const wrapper = chart.canvas.closest(".data-estate-chart");
  if (!wrapper) return;
  const tooltipEl = getOrCreateTooltipEl(wrapper);

  const point = tooltip.dataPoints?.[0];
  if (tooltip.opacity === 0 || !point) {
    tooltipEl.style.opacity = 0;
    return;
  }

  const ds = point.dataset;
  const i = point.dataIndex;
  const label = ds.labels?.[i] || "";
  const color = ds.backgroundColor?.[i] || GREY;
  const count = Number(ds.counts?.[i]) || 0;
  const percent = Number(ds.data?.[i]) || 0;
  const ringTotal = (ds.counts || []).reduce((a, b) => a + (Number(b) || 0), 0);
  const fmt = (n) => n.toLocaleString("en-US");

  const body = ds.notAvailable
    ? `<div style="color:#c9cfe3">Not available for this data source</div>`
    : `<div style="display:flex;align-items:center;gap:8px">
         <span style="width:10px;height:10px;border-radius:50%;flex-shrink:0;background:${color};border:1px solid rgba(255,255,255,0.35)"></span>
         <span style="flex:1;font-weight:600">${label}</span>
       </div>
       <div style="display:flex;align-items:baseline;gap:8px;margin-top:6px">
         <span style="font-size:18px;font-weight:700">${fmt(count)}</span>
         <span style="color:#c9cfe3">${pct(percent)}%</span>
       </div>
       <div style="margin-top:6px;padding-top:6px;border-top:1px solid rgba(255,255,255,0.14);color:#9aa3bd">
         Total: ${fmt(ringTotal)}
       </div>`;

  tooltipEl.innerHTML = `
    <div style="margin-bottom:6px;font-size:10px;font-weight:700;letter-spacing:0.06em;text-transform:uppercase;color:#9aa3bd">${ds.title || ""}</div>
    ${body}`;

  // position relative to the chart wrapper (the public pie sits in its own box)
  const canvasRect = chart.canvas.getBoundingClientRect();
  const wrapperRect = wrapper.getBoundingClientRect();
  const x = canvasRect.left - wrapperRect.left + tooltip.caretX;
  const y = canvasRect.top - wrapperRect.top + tooltip.caretY;

  // open to the left of the pointer so the card edge never clips it
  tooltipEl.style.left = `${x - 14}px`;
  tooltipEl.style.top = `${Math.max(y, 62)}px`;
  tooltipEl.style.transform = "translate(-100%, -50%)";
  tooltipEl.style.opacity = 1;
};

// ring = [{label, count, percent}] -> chart.js dataset (count shown in the tooltip, percent drives the arc)
const buildRing = (ring = [], extra = {}) => ({
  labels: ring.map((r) => LABELS[r.label] || r.label),
  counts: ring.map((r) => Number(r.count) || 0),
  data: ring.map((r) => Number(r.percent) || 0),
  backgroundColor: ring.map((r) => COLORS[r.label] || GREY),
  weight: 1,
  spacing: 4,
  ...extra,
});

const tooltipLabel = (context) => {
  const ds = context.dataset;
  const label = ds.labels?.[context.dataIndex] || context.label || "";
  return `${label}: ${numberFormatter(ds.counts?.[context.dataIndex] ?? 0)}`;
};

const chartOptions = (cutout, isNTFSPublic) => ({
  responsive: true,
  maintainAspectRatio: false,
  devicePixelRatio: window.devicePixelRatio || 1,
  resizeDelay: 0,
  cutout,
  plugins: {
    legend: { display: false },
    datalabels: { display: false },
    tooltip: {
      enabled: false,
      external: externalTooltipHandler,
      callbacks: {
        label: isNTFSPublic ? () => "Public: N/A" : tooltipLabel,
      },
    },
  },
  interaction: { mode: "nearest", intersect: true },
});

function DataEstateSizeCard() {
  const dispatch = useDispatch();

  const dataSourceType = useSelector(
    (state) => state.dataTagReducer.selectedDataSource?.type,
  );
  const dataSourceId = useSelector(
    (state) => state.dataTagReducer.selectedDataSource?.id,
  );
  const isDataBase = useSelector((state) => state.dataTagReducer.isDatabase);
  const isApplication = useSelector((state) => state.dataTagReducer.isApplication);
  const overview = useSelector((state) => state.dataTagReducer.dataEstateOverview);
  const sizeData = useSelector((state) => state.dataTagReducer.sizeCardData);

  const isNTFS = dataSourceType === "NTFS";
  // public access is not available for NTFS or for application (ticket) sources
  const publicNA = isNTFS || !!isApplication;
  const card2 = overview?.card2;

  // the overview API drives file data sources; the old size API is only needed for databases
  useEffect(() => {
    if (dataSourceId && isDataBase) {
      dispatch({ type: FETCH_SIZE_CARD_DATA, payload: dataSourceId });
    }
  }, [dataSourceId, isDataBase]);

  const view = useMemo(() => {
    if (!isDataBase) {
      if (!card2) return null;
      return {
        total: Number(card2.totalFiles) || 0,
        sizeText: filesize((Number(card2.totalSizeGb) || 0) * 1024 ** 3, {
          standard: "jedec",
        }),
        sensitivePct: pct(card2.sensitivePercent),
        regulatedPct: pct(card2.regulatedPercent),
        publicPct: pct(card2.publicPercent),
        processing: card2.processingRing,
        sensitivity: card2.sensitivityRing,
        regulation: card2.regulationRing,
        publicRing: card2.publicRing,
        tagPct: successTaggedPct(card2.tagRing, card2.sensitivityRing),
        tagRing: buildTagRing(
          card2.tagRing,
          card2.totalTaggedFiles,
          card2.totalFiles,
        ),
      };
    }

    // database: keep the previous behaviour (columns / tables)
    if (!sizeData) return null;
    const total = Number(sizeData?.totalColumns?.[0]?.["DataguardDbViewTotal.totalColumns"] || 0);
    const sensitive = Number(sizeData?.sensitiveColumns?.[0]?.["DataguardDbViewTotal.sensitiveColumns"] || 0);
    const regulated = Number(sizeData?.regulatedTables?.[0]?.["DataguardDbViewTotal.regulatedTables"] || 0);
    const p = (n) => (total > 0 ? (n / total) * 100 : 0);
    const ring = (a, b, c, d) => [
      { label: a, count: c, percent: p(c) },
      { label: b, count: Math.max(0, total - c), percent: Math.max(0, 100 - p(c)) },
    ];
    return {
      total,
      sizeText: "",
      sensitivePct: pct(p(sensitive)),
      regulatedPct: pct(p(regulated)),
      publicPct: pct(p(total)),
      processing: ring("PROCESSED", "NOT_PROCESSED", 0),
      sensitivity: ring("SENSITIVE", "NOT_PROCESSED", sensitive),
      regulation: ring("REGULATED", "NOT_REGULATED", regulated),
      publicRing: ring("PUBLIC", "NOT_PUBLIC", 0),
    };
  }, [isDataBase, card2, sizeData]);

  if (!view) {
    return <div>Loading...</div>;
  }

  // tagging only exists for NTFS file sources, so the tag ring + row are NTFS-only
  const showTags = isNTFS && !isDataBase && !!view.tagRing;

  const doughnutData = {
    datasets: [
      buildRing(view.processing, { title: "Processing" }),
      buildRing(view.sensitivity, { title: "Sensitivity" }),
      buildRing(view.regulation, { title: "Regulation" }),
      ...(showTags ? [buildRing(view.tagRing, { title: "Tagging" })] : []),
    ],
  };

  const pieRing = buildRing(view.publicRing, {
    weight: undefined,
    spacing: 0,
    title: "Public access",
    notAvailable: publicNA,
  });
  const publicPieData = {
    labels: pieRing.labels,
    datasets: [{ ...pieRing, borderWidth: 0, cutout: "65%" }],
  };

  return (
    <div className="size-card">
      <div className="card-header size">
        <div>{isDataBase ? "Schema" : isApplication ? "Tickets" : "Size"}</div>
        {!isDataBase && !isApplication && (
          <div className="size-display">{view.sizeText}</div>
        )}
      </div>

      <div className="sizeCardLowerContainer">
        <div className="details">
          <div className="row">
            <div className="circle total"></div>
            <span>Total</span>
            <strong>{numberFormatter(view.total)}</strong>
          </div>
          <div className="row">
            <div className="circle sensitive"></div>
            <span>Sensitive</span>
            <strong>{view.sensitivePct}%</strong>
          </div>
          <div className="row">
            <div className="circle regulated"></div>
            <span>Regulated</span>
            <strong>{view.regulatedPct}%</strong>
          </div>
          {showTags && (
            <div className="row">
              <div className="circle tagged"></div>
              <span>Tagged</span>
              <strong>{view.tagPct}%</strong>
            </div>
          )}
          <div className="row">
            <div className="circle pda"></div>
            <span>Public</span>
            <strong>{publicNA ? "N/A" : `${view.publicPct}%`}</strong>
          </div>
        </div>

        <div className="chart data-estate-chart">
          <Doughnut data={doughnutData} options={chartOptions("42%", false)} />
          <div className="processing-chart">
            <Doughnut data={publicPieData} options={chartOptions("0%", publicNA)} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default DataEstateSizeCard;